import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { Subject, of } from 'rxjs';
import { catchError, takeUntil } from 'rxjs/operators';
import { BsModalService, ModalOptions } from 'ngx-bootstrap/modal';
import { TwilioSmsService, SendSmsPayload } from 'src/app/core/services/twilio-sms.service';
import {
  SendVoidfixMessagePayload,
  VoidfixSmsService,
} from 'src/app/core/services/voidfix-sms.service';
import { MessageTemplatesService, MessageTemplate } from 'src/app/core/services/message-templates.service';
import { ApplicationContextService } from 'src/app/core/services/application-context.service';
import {
  getSelectedCompanyIMessageLineId,
  isSelectedCompanyVoidfixEnabled,
} from 'src/app/core/utils/organization-services-license.util';
import { getLaunchRequestedCompanyId } from 'src/app/core/utils/clinic-launch-context.util';
import { EventService } from 'src/app/core/services/event.service';
import { ApplicationCacheService } from 'src/app/core/services/application-cache.service';
import { BroadcastEventTypes } from 'src/app/core/models/lookup.model';
import { SavedRepliesModalComponent } from '../saved-replies-modal/saved-replies-modal.component';

@Component({
  selector: 'app-send-sms',
  templateUrl: './send-sms.component.html',
  styleUrls: ['./send-sms.component.scss']
})
export class SendSmsComponent implements OnInit, OnDestroy, OnChanges {
  @ViewChild('fileInput') fileInput?: ElementRef<HTMLInputElement>;

  @Input() patientId: string = '';
  /** Engage lead thread — send payload uses referralId when patientId is absent. */
  @Input() referralId: string = '';
  @Input() loggedInUserName: string = '';
  /** E.164 or display phone for Voidfix `recipient` when the org uses Voidfix. */
  @Input() recipientPhone: string = '';
  /** CRM Activity tab: Notes-style “Send a message” strip instead of the compact SMS bar. */
  @Input() quickComposer = false;
  @Output() messageSent = new EventEmitter<void>();

  messageText: string = '';
  isSending: boolean = false;
  attachedFile: File | null = null;
  attachedFilePreview: string | null = null;
  isDragOver: boolean = false;
  showTemplateDropdown: boolean = false;
  messageTemplates: MessageTemplate[] = [];
  isLoadingTemplates: boolean = false;
  templateSearchFilter: string = '';
  hasJoinedConversation: boolean | null = null;
  isJoining: boolean = false;
  private readonly destroy$ = new Subject<void>();
  private currentUserId: string = '';
  private skipNextInputCheck: boolean = false;
  private isVoidfixEnabled = false;
  private voidfixIMessageLineId: string | null = null;

  /** Same as wa-engage-v2 `message.component` MMS attach rules. */
  readonly allowedFileTypes = [
    'image/jpeg',
    'image/png',
    'image/gif',
    'image/webp',
    'application/pdf',
  ];
  readonly maxAttachmentBytes = 5 * 1024 * 1024;

  /**
   * Engage: "Assign to" is Everyone (no specific user id) — Join stays disabled and the SMS
   * composer stays hidden even if the check-joined API returns true.
   */
  get isAssignedToEveryoneOnReferral(): boolean {
    const rid = this.effectiveReferralId();
    const assigned = (
      this._applicationCacheService.applicationCache.engageReferralAssignedToUserId || ''
    ).trim();
    return !!rid && !assigned;
  }

  /** Dims the SMS host when Engage lead is still "Everyone" (parent input area looks disabled). */
  @HostBinding('class.send-sms--patient-advisor-everyone')
  get hostPatientAdvisorEveryoneClass(): boolean {
    return this.isEngageReferralThread && this.isAssignedToEveryoneOnReferral;
  }

  @HostBinding('class.send-sms--quick-composer')
  get hostQuickComposerClass(): boolean {
    return this.quickComposer;
  }

  get hasQuickDraft(): boolean {
    return !!(this.messageText || '').trim() || !!this.attachedFile;
  }

  /** Engage lead / referral thread (not clinic-only SMS). */
  get isEngageReferralThread(): boolean {
    return !!this.effectiveReferralId();
  }

  /**
   * SMS composer: clinic/patient threads after join. Engage: only when assigned to a specific user
   * (not Everyone) and the current user has joined — never show composer for Everyone.
   */
  get showSmsComposer(): boolean {
    if (this.isEngageReferralThread && this.isAssignedToEveryoneOnReferral) {
      return false;
    }
    return this.hasJoinedConversation === true;
  }

  /**
   * Engage + Everyone: always show Join (disabled), joined or not. Engage + specific assignee:
   * show enabled Join until joined; then composer. Clinic: Join until joined.
   */
  get showJoinNowButton(): boolean {
    if (this.showSmsComposer) {
      return false;
    }
    if (this.isEngageReferralThread) {
      if (this.isAssignedToEveryoneOnReferral) {
        return true;
      }
      return this.hasJoinedConversation === false;
    }
    return this.hasJoinedConversation === false;
  }

  /** Referral id for Engage thread; do not use cache when a clinic patient is selected (avoids stale id). */
  private effectiveReferralId(): string {
    const fromInput = (this.referralId || '').trim();
    if (fromInput) {
      return fromInput;
    }
    if ((this.patientId || '').trim()) {
      return '';
    }
    return (this._applicationCacheService.applicationCache.engageNotesReferralId || '').trim();
  }

  constructor(
    private readonly _twilioSmsService: TwilioSmsService,
    private readonly _voidfixSmsService: VoidfixSmsService,
    private readonly _messageTemplatesService: MessageTemplatesService,
    private readonly _appContextService: ApplicationContextService,
    private readonly _modalService: BsModalService,
    private readonly _eventService: EventService,
    private readonly _applicationCacheService: ApplicationCacheService,
    private readonly _cdr: ChangeDetectorRef
  ) {
    this._appContextService.context
      .pipe(takeUntil(this.destroy$))
      .subscribe((context) => {
        this.currentUserId = context?.currentlyLoggedInUser?.id || '';
        const companyId =
          getLaunchRequestedCompanyId() ||
          context?.currentlyLoggedInUser?.companyId ||
          '';
        this.isVoidfixEnabled = isSelectedCompanyVoidfixEnabled(
          context?.userCompanies,
          companyId
        );
        this.voidfixIMessageLineId = getSelectedCompanyIMessageLineId(
          context?.userCompanies,
          companyId
        );
      });
  }

  ngOnInit(): void {
    this._eventService
      .on(BroadcastEventTypes.EngageReferralAssignmentChanged)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        // Do not re-call checkPatientJoined / checkReferralJoined here — it can return false
        // briefly and clear a valid joined state after assign-to updates, which re-shows Join now.
        this._cdr.markForCheck();
      });

    this.refreshJoinStatus();
    this.markReferralMessagesActiveIfApplicable();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['referralId'] || changes['patientId']) {
      this.refreshJoinStatus();
      this.markReferralMessagesActiveIfApplicable();
    }
  }

  /**
   * Conversation-participants: use clinic `patientId` when the referral is linked to a patient
   * (check-patient / join with patientId). New lead with no patient uses `referralId`
   * (check-referral / join with referralId).
   */
  private refreshJoinStatus(): void {
    const pid = (this.patientId || '').trim();
    if (pid) {
      this.checkPatientJoinStatus();
      return;
    }
    const rid = this.effectiveReferralId();
    if (rid) {
      this.checkReferralJoinStatus(rid);
    } else {
      this.hasJoinedConversation = true;
    }
  }

  /** POST `api/v1/twilio/messages/mark-active/{patientId}` when a patient/referral thread is in context. */
  private markReferralMessagesActiveIfApplicable(): void {
    const id = (this.patientId || "").trim() || this.effectiveReferralId();
    if (!id) {
      return;
    }
    this._twilioSmsService
      .markReferralMessagesActive(id)
      .pipe(takeUntil(this.destroy$), catchError(() => of(null)))
      .subscribe();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private checkPatientJoinStatus(): void {
    const pid = (this.patientId || '').trim();
    if (!pid) {
      this.hasJoinedConversation = null;
      return;
    }
    this.hasJoinedConversation = null;
    this._twilioSmsService
      .checkPatientJoined(pid)
      .pipe(
        takeUntil(this.destroy$),
        catchError(() => of(false))
      )
      .subscribe((joined) => {
        this.hasJoinedConversation = joined;
      });
  }

  private checkReferralJoinStatus(referralId: string): void {
    this.hasJoinedConversation = null;
    this._twilioSmsService
      .checkReferralJoined(referralId)
      .pipe(
        takeUntil(this.destroy$),
        catchError(() => of(false))
      )
      .subscribe((joined) => {
        this.hasJoinedConversation = joined;
      });
  }

  joinConversation(): void {
    if (this.isJoining || this.isAssignedToEveryoneOnReferral) {
      return;
    }
    const pid = (this.patientId || '').trim();
    const rid = this.effectiveReferralId();
    if (!pid && !rid) {
      return;
    }
    this.isJoining = true;
    const req = pid
      ? this._twilioSmsService.joinConversation(pid)
      : this._twilioSmsService.joinConversationByReferral(rid);
    req.pipe(takeUntil(this.destroy$)).subscribe({
      next: () => {
        this.hasJoinedConversation = true;
        this.isJoining = false;
        this._eventService.broadcast(BroadcastEventTypes.SmsMessagesRefresh);
},
      error: (err) => {
        this.isJoining = false;
},
    });
  }

  async sendMessage(): Promise<void> {
    if (!this.messageText.trim() && !this.attachedFile) {
return;
    }

    const pid = (this.patientId || '').trim();
    const rid = (this.referralId || '').trim();
    if (!pid && !rid) {
return;
    }

    this.isSending = true;

    try {
      const text = this.messageText.trim();

      if (this.isVoidfixEnabled) {
        await this.sendVoidfixMessage(text);
        return;
      }

      const body = text || (this.attachedFile ? ' ' : '');
      const payload: SendSmsPayload = {
        referralId: rid,
        patientId: pid,
        body,
        senderName: this.loggedInUserName
      };

      // Handle file attachment
      if (this.attachedFile) {
        const base64 = await this.fileToBase64(this.attachedFile);
        payload.mediaBase64 = base64;
        payload.mediaFilename = this.attachedFile.name;
        payload.mediaContentType = this.attachedFile.type;
      }

      this._twilioSmsService.sendMessage(payload)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () => {
            this.onMessageSendSuccess();
          },
          error: () => {
            this.isSending = false;
          }
        });
    } catch (error) {
      this.isSending = false;
    }
  }

  private async sendVoidfixMessage(text: string): Promise<void> {
    const recipient = this._voidfixSmsService.toE164Recipient(this.recipientPhone);
    if (!recipient) {
      this.isSending = false;
      return;
    }

    const payload: SendVoidfixMessagePayload = {
      recipient,
      senderName: this.loggedInUserName,
    };

    if (text) {
      payload.message = text;
    }
    if (this.voidfixIMessageLineId) {
      payload.iMessageLineId = this.voidfixIMessageLineId;
    }
    if (this.attachedFile) {
      payload.mediaBase64 = await this.fileToBase64(this.attachedFile);
      payload.mediaFilename = this.attachedFile.name;
      payload.mediaContentType = this.attachedFile.type;
    }

    if (!payload.message && !payload.mediaBase64) {
      this.isSending = false;
      return;
    }

    this._voidfixSmsService
      .sendMessage(payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.onMessageSendSuccess();
        },
        error: () => {
          this.isSending = false;
        },
      });
  }

  private onMessageSendSuccess(): void {
    this.messageText = '';
    this.removeAttachment();
    this.messageSent.emit();
    this._eventService.broadcast(BroadcastEventTypes.SmsMessagesRefresh);
    this.isSending = false;
  }

  fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const base64String = (reader.result as string).split(',')[1]; // Remove data:image/...;base64, prefix
        resolve(base64String);
      };
      reader.onerror = (error) => reject(error);
    });
  }

  onFileSelect(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.handleFile(input.files[0]);
    }
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;

    if (event.dataTransfer?.files && event.dataTransfer.files.length > 0) {
      this.handleFile(event.dataTransfer.files[0]);
    }
  }

  onPaste(event: ClipboardEvent): void {
    const items = event.clipboardData?.items;
    if (items) {
      for (let i = 0; i < items.length; i++) {
        if (items[i].kind === 'file') {
          const file = items[i].getAsFile();
          if (file) {
            this.handleFile(file);
            break;
          }
        }
      }
    }
  }

  openFilePicker(): void {
    this.fileInput?.nativeElement?.click();
  }

  handleFile(file: File): void {
    if (!this.allowedFileTypes.includes(file.type)) {
return;
    }
    if (file.size > this.maxAttachmentBytes) {
return;
    }

    this.attachedFile = file;

    // Create preview for images
    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e) => {
        this.attachedFilePreview = e.target?.result as string;
      };
      reader.readAsDataURL(file);
    } else {
      this.attachedFilePreview = null;
    }
  }

  removeAttachment(): void {
    this.attachedFile = null;
    this.attachedFilePreview = null;
    if (this.fileInput?.nativeElement) {
      this.fileInput.nativeElement.value = '';
    }
  }

  onKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      this.showTemplateDropdown = false;
      return;
    }
    if (event.key === 'ArrowDown' && this.showTemplateDropdown && this.filteredTemplates.length > 0) {
      event.preventDefault();
      return;
    }
    if (event.key === 'Enter' && !event.shiftKey) {
      if (this.showTemplateDropdown && this.filteredTemplates.length > 0) {
        this.selectTemplate(this.filteredTemplates[0]);
        event.preventDefault();
        return;
      }
      event.preventDefault();
      if (!this.isSending && (this.messageText.trim() || this.attachedFile)) {
        this.sendMessage();
      }
    }
  }

  onMessageInput(): void {
    if (this.skipNextInputCheck) {
      this.skipNextInputCheck = false;
      return;
    }
    const text = this.messageText;
    const lastHashIndex = text.lastIndexOf('#');
    if (lastHashIndex !== -1) {
      const afterHash = text.substring(lastHashIndex + 1);
      if (!afterHash.includes(' ') && !afterHash.includes('\n')) {
        this.templateSearchFilter = afterHash;
        this.loadTemplatesAndShowDropdown();
        return;
      }
    }
    this.showTemplateDropdown = false;
  }

  private loadTemplatesAndShowDropdown(): void {
    if (!this.currentUserId) return;
    this.showTemplateDropdown = true;
    this.isLoadingTemplates = true;
    this._messageTemplatesService.getMessageTemplates(this.currentUserId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (templates) => {
          this.messageTemplates = templates || [];
          this.isLoadingTemplates = false;
        },
        error: () => {
          this.messageTemplates = [];
          this.isLoadingTemplates = false;
        }
      });
  }

  get filteredTemplates(): MessageTemplate[] {
    if (!this.templateSearchFilter) return this.messageTemplates;
    const q = this.templateSearchFilter.toLowerCase();
    return this.messageTemplates.filter(t =>
      (t.title || '').toLowerCase().includes(q) ||
      (t.response_message || '').toLowerCase().includes(q)
    );
  }

  selectTemplate(template: MessageTemplate): void {
    this.skipNextInputCheck = true;
    const text = this.messageText;
    const lastHashIndex = text.lastIndexOf('#');
    const filterLen = this.templateSearchFilter.length;
    const beforeHash = lastHashIndex > 0 ? text.substring(0, lastHashIndex) : '';
    const afterTemplate = text.substring(lastHashIndex + 1 + filterLen);
    const separator = beforeHash && (beforeHash.endsWith(' ') || beforeHash.endsWith('\n')) ? '' : (beforeHash ? ' ' : '');
    this.messageText = beforeHash + separator + (template.response_message || '') + afterTemplate;
    this.showTemplateDropdown = false;
    this.templateSearchFilter = '';
  }

  closeTemplateDropdown(): void {
    this.showTemplateDropdown = false;
    this.templateSearchFilter = '';
  }

  discardQuickDraft(): void {
    this.messageText = '';
    this.removeAttachment();
    this.closeTemplateDropdown();
  }

  openSavedRepliesModal(): void {
    if (!this.currentUserId) return;
    this.closeTemplateDropdown();
    const initialState = {
      templates: this.messageTemplates,
      currentUserId: this.currentUserId
    };
    const options: ModalOptions = {
      initialState,
      class: 'modal-lg modal-dialog-centered',
      ignoreBackdropClick: false
    };
    const ref = this._modalService.show(SavedRepliesModalComponent, options);
    ref.content?.useTemplate?.subscribe((template: MessageTemplate) => {
      this.appendTemplateToMessage(template);
    });
  }

  appendTemplateToMessage(template: MessageTemplate): void {
    let text = this.messageText;
    const lastHashIndex = text.lastIndexOf('#');
    if (lastHashIndex !== -1) {
      text = text.substring(0, lastHashIndex).trimEnd();
    }
    const sep = text ? ' ' : '';
    this.messageText = text + sep + (template.response_message || '');
  }
}
